/**
 * ADR-0032 Part 3, gate item 3 (ENG-9684) — the approved Composio egress address set.
 *
 * The data lives in `composio-approved-egress.json` so that a shell script and a Lambda
 * can read the SAME bytes. See that file's `$comment` for why it is a repo file and not
 * SSM, and for why the comparison is on address only.
 *
 * This module is the typed half. It deliberately does no I/O: the JSON is bundled at
 * build time, so a Lambda cannot be missing it at runtime the way a `readFileSync` of a
 * path relative to `__dirname` can be.
 */
import approved from './composio-approved-egress.json' with { type: 'json' };

export interface ApprovedEgressAddress {
  /** The public IPv4 address. This, and only this, is what the allowlist holds. */
  readonly ip: string;
  readonly stage: string;
  /**
   * Where the address actually lives (ENG-10687). Per-address, because this set spans two
   * accounts and two regions: the hosts NATs in 711726113003 / ap-southeast-2 and the
   * control-plane bridge in 109039361030 / us-east-2. Informational like `seenAsGateway` —
   * the comparison is still on `ip` alone.
   */
  readonly account: string;
  readonly region: string;
  /** Informational. NOT compared — a rebuilt NAT re-adopting the same EIP is the safe case. */
  readonly seenAsGateway: string;
  readonly note: string;
}

/**
 * The ONE account and region the drift detector enumerates — NOT the scope of this file
 * (ENG-10687). The approved set deliberately spans two accounts; these two constants are
 * the detector's own pin, which is why they stayed hosts-account values when the bridge
 * addresses landed here. For "where does this address live", read the per-address
 * `account` / `region` fields instead.
 */
export const APPROVED_EGRESS_ACCOUNT: string = approved.account;
export const APPROVED_EGRESS_HOME_REGION: string = approved.homeRegion;
export const APPROVED_EGRESS_ADDRESSES: readonly ApprovedEgressAddress[] = approved.addresses;

/**
 * The approved set, as a Set for membership tests.
 *
 * Throws on an empty set rather than returning one. An empty approved set makes EVERY
 * observed address unapproved, so a detector would report the entire fleet as drifting
 * and an operator would read it as a broken check rather than a real one — while a
 * *transport* consumer reading the same empty set would allow nothing. Neither caller has
 * a safe interpretation, so the failure belongs here, once, where it is unambiguous.
 */
export function approvedEgressAddresses(): ReadonlySet<string> {
  const ips = APPROVED_EGRESS_ADDRESSES.map((a) => a.ip.trim()).filter((ip) => ip.length > 0);
  if (ips.length === 0) {
    throw new Error(
      'composio-approved-egress.json declares no addresses — refusing to return an empty approved set',
    );
  }
  return new Set(ips);
}

/**
 * One address approved for a SINGLE org, because its agents egress from an account
 * we do not operate (ENG-10718).
 *
 * Same shape as `ApprovedEgressAddress` with one difference: `region` is nullable.
 * For a BYO address the region is frequently not recorded and cannot be enumerated —
 * the `augmented-byo-provisioner` role grants neither `DescribeRouteTables` nor
 * `DescribeNatGateways`, which are the two calls that attribute a host's egress to an
 * address. `null` says so. A guessed region would be a claim this file makes without
 * evidence, and the comparison is on `ip` alone, so it costs nothing to admit.
 */
export interface OrgEgressAddress {
  readonly ip: string;
  readonly stage: string;
  readonly account: string;
  /** `null` = not recorded, and not discoverable from here. Never a guess. */
  readonly region: string | null;
  readonly seenAsGateway: string;
  readonly note: string;
}

export interface OrgEgressRecord {
  /** Informational, like `seenAsGateway`. The UUID key is the identity — slugs change. */
  readonly orgSlug: string;
  readonly addresses: readonly OrgEgressAddress[];
}

/**
 * Per-org approved addresses, keyed by organization UUID.
 *
 * Read by the admin posture page and nothing else. The drift detector and
 * `fleet-egress-enum.sh` read `addresses` and only `addresses`, which is why a per-org
 * address can never be mistaken for a hosts-account one: neither ever sees this map,
 * and neither enumerates the accounts these addresses live in.
 */
export const ORG_EGRESS_ADDRESSES: Readonly<Record<string, OrgEgressRecord>> =
  approved.orgAddresses ?? {};

/**
 * What a single org's row may offer an operator to paste into that org's key.
 *
 * Three outcomes, not two. "We could not count this org's hosts" is NOT the same
 * as "it has none", and collapsing them would make a failed `hosts` read render as a
 * confident, possibly-wrong paste value — the exact shape of the failure this whole
 * feature exists to prevent.
 */
export type OrgAllowlist =
  | {
      readonly kind: 'value';
      /** Comma-joined, no spaces, exactly as Composio's field takes it. */
      readonly allowlistValue: string;
      readonly sharedCount: number;
      readonly orgAddresses: readonly OrgEgressAddress[];
    }
  | {
      readonly kind: 'withheld';
      readonly reason: WithheldReason;
      /** `null` when the count itself is what could not be established. */
      readonly hostCount: number | null;
      /**
       * The addresses these hosts are known to use that are NOT approved for this org.
       * Populated only for `hosts-without-recorded-egress`, and deliberately NOT
       * offered as something to paste: they are observed, not approved. Naming them
       * tells an operator what to take to the approval, which is the next step.
       */
      readonly unapprovedAddresses?: readonly string[];
    };

/**
 * Why a row may not be armed, and the three answers are genuinely different.
 *
 * `host-count-unknown` — the `hosts` read failed or was truncated. Nothing is known.
 * `host-egress-unknown` — the hosts are known, but at least one has no attested
 *   address, so whether the recorded set covers it cannot be decided. This is the
 *   common state for a newly enrolled BYO host that has not called home yet.
 * `hosts-without-recorded-egress` — the hosts are known AND their addresses are known,
 *   and at least one of those addresses is not approved for this org.
 *
 * Collapsing them would put "we have not looked" and "we looked and it is wrong" behind
 * one message, and only the second is a reason to go and record an address.
 */
export type WithheldReason =
  | 'hosts-without-recorded-egress'
  | 'host-egress-unknown'
  | 'host-count-unknown';

/**
 * Compose the value for one org's row: the shared set plus that org's recorded
 * addresses — or nothing at all.
 *
 * WHY WITHHOLDING IS THE POINT. An org whose agents run on a host we do not operate
 * the network for — a BYO host in the customer's AWS account, or a `self` host on
 * hardware we never see — egresses from an address the shared set does not carry. Arming its key with the shared set alone refuses every
 * Composio call those agents make, and the only signal is a production 401 on a key
 * with no management API and no fleet undo. A per-row button IMPLIES the value is that
 * org's set; if it silently is not, the button is a plausible wrong answer, which is
 * worse than no button. So a row with BYO hosts and no recorded address offers nothing.
 *
 * COVERAGE IS CHECKED, NOT ASSUMED (CodeRabbit, PR #6289). An earlier cut compared the
 * host COUNT against "is anything recorded at all", and documented the gap instead of
 * closing it: two hosts on different egress addresses with one address recorded passed,
 * and handed over a copyable value missing the second. A count cannot establish
 * coverage. What can is `host_attested_egress` (ENG-10666) — the addresses each host has
 * actually been seen calling from, which is the only way to learn a BYO host's egress at
 * all, since the provisioner role grants neither `DescribeRouteTables` nor
 * `DescribeNatGateways`. So the caller passes the addresses, and every one of them must
 * be approved for this org.
 *
 * THAT DOES NOT MAKE OBSERVATION INTO APPROVAL. The observed set says what MUST be
 * covered; `orgAddresses` still says what IS approved, and only a human editing that
 * file adds to it. An observed address that is not recorded makes the row withhold and
 * is named in the warning — as the thing to go and approve, never as a value to paste.
 *
 * @param organizationId - the org's UUID, the key into `ORG_EGRESS_ADDRESSES`.
 * @param unaccounted - this org's active hosts that egress from somewhere the shared set
 *   does not cover. NOT only BYO hosts: a `provision_source = 'self'` host is
 *   customer-installed on hardware we never see. `null` means the count could not be
 *   established at all; `requiredAddresses: null` means the hosts are known but their
 *   addresses are not. Both withhold — with different reasons, because only one of them
 *   is fixed by recording an address.
 */
/**
 * The shared set, as a Set. A host whose attested address is already in the SHARED set
 * needs no per-org record — a managed host that briefly appeared unaccounted (a
 * mis-set `provision_source`, say) must not make its org un-armable over an address
 * every key already holds.
 */
const SHARED_IPS = () => approvedEgressAddresses();

export function composeOrgAllowlist(input: {
  readonly organizationId: string;
  readonly unaccounted:
    | null
    | {
        readonly hostCount: number;
        readonly requiredAddresses: readonly string[] | null;
      };
}): OrgAllowlist {
  const recorded = ORG_EGRESS_ADDRESSES[input.organizationId]?.addresses ?? [];
  // USABLE ips, not entries. The JSON is imported directly and nothing validates its
  // contents at runtime, so a record left as `{ ip: " " }` by a bad edit has a non-zero
  // length and contributes NOTHING to the composed value. Counting entries would then
  // pass the withholding check below and hand an org with unaccounted hosts a
  // shared-only value — the precise outcome withholding exists to prevent, arrived at
  // through the guard meant to stop it (CodeRabbit, PR #6289).
  const recordedIps = recorded.map((a) => a.ip.trim()).filter((ip) => ip.length > 0);
  const unaccounted = input.unaccounted;

  // Fail closed on an unusable count. `null` is the caller saying so; a NaN or a
  // negative is a caller that thinks it counted and did not, and both arrive here
  // looking like numbers.
  const count = unaccounted?.hostCount ?? null;
  if (count === null || !Number.isFinite(count) || count < 0) {
    return { kind: 'withheld', reason: 'host-count-unknown', hostCount: null };
  }

  if (count > 0) {
    const required = unaccounted?.requiredAddresses ?? null;
    // Hosts exist and we do not know where they leave from. Nothing here can be
    // decided, and recording an address would not help until one is observed.
    if (required === null) {
      return { kind: 'withheld', reason: 'host-egress-unknown', hostCount: count };
    }
    const approved = new Set(recordedIps);
    const unapproved = [...new Set(required.map((ip) => ip.trim()).filter((ip) => ip.length > 0))]
      .filter((ip) => !approved.has(ip) && !SHARED_IPS().has(ip))
      .sort();
    // A host with an address nobody approved is a host the composed value would refuse.
    // Note this also covers "nothing recorded at all": every required address is then
    // unapproved, so the zero-recorded case needs no separate branch.
    if (unapproved.length > 0) {
      return {
        kind: 'withheld',
        reason: 'hosts-without-recorded-egress',
        hostCount: count,
        unapprovedAddresses: unapproved,
      };
    }
  }

  // `approvedEgressAddresses()` rather than mapping APPROVED_EGRESS_ADDRESSES here, so
  // an empty shared set throws once, there, instead of silently composing a value made
  // only of one org's BYO address — which would be a key that locks out the whole fleet.
  const shared = [...SHARED_IPS()];
  const ips = [...shared];
  for (const ip of recordedIps) {
    // Deduped, order preserved: a per-org entry that duplicates a shared one is a
    // bookkeeping mistake, not a reason to paste the same address twice.
    if (!ips.includes(ip)) ips.push(ip);
  }
  return {
    kind: 'value',
    allowlistValue: ips.join(','),
    sharedCount: shared.length,
    orgAddresses: recorded,
  };
}
